import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import GuestEditDialog from "@/app/admin/_common/guestForm/GuestEditDialog";
import { cn } from "@/app/_utils/cn";
import { dayjsTz, dayjsUtcToTz } from "@/app/_utils/dayjs";
import { getSignupsTimestamps, isNewSignupEvent } from "@/app/_utils/guestSignups";
import { Users, BellOff, Star } from "lucide-react";
import type { LinkSignupGuest } from "../../linksPageModel";

const SIGNUP_BADGE_CLASSES = {
  new: "border-emerald-200 bg-emerald-50 text-emerald-700",
  repeat: "border-orange-200 bg-orange-50 text-orange-700",
};
const SIGNUP_DOT_CLASSES = {
  new: "bg-emerald-500",
  repeat: "bg-orange-500",
};

export default function LinkSignupsTable({
  guests,
  onGuestSaved,
}: {
  guests: LinkSignupGuest[];
  onGuestSaved: () => void;
}) {
  const navigate = useNavigate();
  const [selectedGuest, setSelectedGuest] = useState<LinkSignupGuest | null>(null);

  return (
    <div className="flex min-h-[420px] flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className="min-w-[700px] table-fixed"
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[6%] text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Pos
            </TableHead>
            <TableHead className="h-8 w-[24%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Name
            </TableHead>
            <TableHead className="h-8 w-16 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Shows
            </TableHead>
            <TableHead className="h-8 w-20 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Signups
            </TableHead>
            <TableHead className="h-8 w-[15%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Instagram
            </TableHead>
            <TableHead className="h-8 w-[20%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Email
            </TableHead>
            <TableHead className="h-8 w-[17%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Signup Date
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {guests.length === 0 ? (
            <TableRow className="border-0 hover:bg-transparent">
              <TableCell colSpan={7} className="p-0">
                {/* An explicit height, not h-full: the wrapper's min-height is
                    not a definite height, so percentage heights collapse and
                    the empty state sticks to the top of the table. */}
                <div className="flex min-h-[380px] items-center justify-center">
                  <CuoEmpty icon={Users} text="No signups" />
                </div>
              </TableCell>
            </TableRow>
          ) : (
            guests.map((guest) => {
              const isNew = isNewSignupEvent(guest.signups);
              const signupAt = dayjsUtcToTz(guest.signup_date);
              const showBadge =
                signupAt.isAfter(dayjsTz("2024-12-18")) &&
                !signupAt.isBefore(dayjsTz().subtract(30, "day"));

              return (
                <TableRow
                  key={guest.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedGuest(guest)}
                >
                  <TableCell className="py-1.5 text-center text-[12.5px] text-muted-foreground">
                    {guest.index ?? "-"}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="truncate text-[13px] text-foreground hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/guests/${guest.id}`);
                        }}
                      >
                        {guest.first_name} {guest.last_name}
                      </span>
                      {showBadge && (
                        <Badge
                          variant="outline"
                          className={`gap-1 rounded-[7px] px-1.5 py-0 text-[10px] font-normal ${
                            SIGNUP_BADGE_CLASSES[isNew ? "new" : "repeat"]
                          }`}
                        >
                          <span
                            className={`size-1.5 flex-none rounded-full ${
                              SIGNUP_DOT_CLASSES[isNew ? "new" : "repeat"]
                            }`}
                          />
                          {isNew ? "New" : "Repeat"}
                        </Badge>
                      )}
                      {!guest.subscribed && (
                        <BellOff className="size-3.5 flex-none text-muted-foreground" />
                      )}
                      {(guest.num_shows ?? 0) >= 2 && (
                        <Star className="size-3.5 flex-none fill-amber-400 text-amber-400" />
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="py-1.5 text-center text-[12.5px] text-muted-foreground">
                    {(guest.num_shows ?? 0) > 0 ? guest.num_shows : "-"}
                  </TableCell>
                  <TableCell className="py-1.5 text-center text-[12.5px] text-muted-foreground">
                    {getSignupsTimestamps(guest.signups).length || "-"}
                  </TableCell>
                  <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                    {guest.instagram || "-"}
                  </TableCell>
                  <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                    {guest.email || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                    {dayjsUtcToTz(guest.signup_date).format("MMM D, YYYY h:mm a")}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <GuestEditDialog
        guest={selectedGuest}
        open={selectedGuest !== null}
        onClose={() => setSelectedGuest(null)}
        getData={onGuestSaved}
      />
    </div>
  );
}
